import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, List, Network, Users } from 'lucide-react';
import { getAuthToken, useAuth } from '../../auth/context/AuthContext';
import PasswordField from '../../../shared/components/PasswordField';
import ModalError from '../../../shared/components/ModalError';
import OrgChart from '../components/OrgChart';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/admin`;

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin:       'Admin',
  manager:     'Manager',
  team_lead:   'Team Lead',
  recruiter:   'Recruiter',
};

const ROLE_COLORS = {
  super_admin: '#f87171',
  admin:       '#a78bfa',
  manager:     '#38bdf8',
  team_lead:   '#34d399',
  recruiter:   '#94a3b8',
};

const authHeaders = () => ({
  Authorization: `Bearer ${getAuthToken()}`,
  'Content-Type': 'application/json',
});

function RoleBadge({ role }) {
  const color = ROLE_COLORS[role] || '#94a3b8';
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999,
      color, background: `${color}1f`, border: `1px solid ${color}55`,
      whiteSpace: 'nowrap',
    }}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

/**
 * People who may lead someone with `role`: the roles the server allows, active,
 * and never the person themselves. `extraExclude` drops more ids.
 */
function leaderOptions(users, rules, role, excludeId, extraExclude = []) {
  const allowed = rules[role] || [];
  return users.filter(u =>
    allowed.includes(u.role) && u.is_active && u.id !== excludeId && !extraExclude.includes(u.id));
}

function LeaderSelect({ value, onChange, options, allowTop, topLabel = 'Directly under the admin' }) {
  return (
    <select className="form-select" value={value} onChange={e => onChange(e.target.value)}>
      {allowTop && <option value="">{topLabel}</option>}
      {options.map(u => (
        <option key={u.id} value={u.id}>{u.name} · {ROLE_LABELS[u.role] || u.role}</option>
      ))}
    </select>
  );
}

const VIEW_KEY = 'team-view';

function readView() {
  try { return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'org'; } catch { return 'org'; }
}

export default function Team() {
  const { user: me } = useAuth();
  // The org chart answers "who is under whom"; the list is for scanning emails.
  const [view, setViewState] = useState(readView);
  const setView = (v) => {
    setViewState(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* remembered for this visit only */ }
  };

  const [users, setUsers]       = useState([]);
  const [companyName, setCompany] = useState('');
  const [assignable, setAssignable] = useState([]);
  const [canRemove, setCanRemove] = useState(false);
  const [canMove, setCanMove]   = useState(false);
  const [rules, setRules]       = useState({});
  const [removeFor, setRemoveFor] = useState(null);
  const [handover, setHandover] = useState({ team: '', jobs: '' });
  const [moveFor, setMoveFor]   = useState(null);
  const [moveTo, setMoveTo]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState('');
  const [msg, setMsg]           = useState('');
  const [search, setSearch]     = useState('');

  const [showAdd, setShowAdd]   = useState(false);
  const [form, setForm]         = useState({ email: '', name: '', password: '', role: '', reports_to_id: '' });
  const [busy, setBusy]         = useState(false);

  const [pwFor, setPwFor]       = useState(null);   // user object
  const [newPw, setNewPw]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await fetch(`${API}/users`, { headers: authHeaders() });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `Failed to load team (${res.status})`);
      }
      const data = await res.json();
      setUsers(data.users || []);
      setCompany(data.company_name || '');
      setAssignable(data.assignable_roles || []);
      setCanRemove(Boolean(data.can_remove_users));
      setCanMove(Boolean(data.can_move_users));
      setRules(data.reports_to_roles || {});
      setForm(f => ({ ...f, role: f.role || (data.assignable_roles || [])[0] || '' }));
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const listed = q ? users.filter(u => `${u.name} ${u.email}`.toLowerCase().includes(q)) : users;

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const addUser = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/users`, {
        method: 'POST', headers: authHeaders(),
        // An empty choice means "directly under the admin" for an admin; for a
        // manager or team lead it lets the server default to themselves.
        body: JSON.stringify({ ...form, reports_to_id: form.reports_to_id || null, send_welcome: true }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Could not add user');
      setShowAdd(false);
      setForm({ email: '', name: '', password: '', role: assignable[0] || '', reports_to_id: '' });
      flash(`Added ${d.email}`);
      load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const patchUser = async (u, body, okMsg) => {
    setErr('');
    try {
      const res = await fetch(`${API}/users/${u.id}`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Update failed');
      flash(okMsg);
      load();
    } catch (e) { setErr(e.message); }
  };

  const removeUser = async (u) => {
    setErr('');
    try {
      const params = new URLSearchParams();
      if (handover.team) params.set('move_team_to', handover.team);
      if (handover.jobs) params.set('move_jobs_to', handover.jobs);
      const res = await fetch(`${API}/users/${u.id}?${params}`, { method: 'DELETE', headers: authHeaders() });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Delete failed');
      flash(d.message);
      setRemoveFor(null);
      load();
    } catch (e) { setErr(e.message); }
  };

  const moveUser = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/users/${moveFor.id}/reports-to`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ reports_to_id: moveTo || null }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Could not move');
      setMoveFor(null);
      flash(d.message);
      load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const openRemove = (u) => {
    setErr('');
    setHandover({ team: '', jobs: '' });
    setRemoveFor(u);
  };

  // Where a leaving person's direct reports can go: a leader every one of them
  // may report to, outside the person themselves.
  const teamOf = (u) => users.filter(m => m.reports_to_id === u.id);
  const handoverLeaders = (u) => {
    const team = teamOf(u);
    return users.filter(c => c.id !== u.id && c.is_active
      && team.every(m => (rules[m.role] || []).includes(c.role)));
  };
  const handoverRecruiters = (u) => users.filter(c => c.id !== u.id && c.is_active && c.role === 'recruiter');

  const savePassword = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/users/${pwFor.id}/reset-password`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ new_password: newPw }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Could not set password');
      setPwFor(null); setNewPw('');
      flash(d.message);
      load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  // Password and Remove are admin-only; a manager assigns roles but must not
  // lock a colleague out or take over their account. The server enforces this too.
  const renderActions = (u) => {
    const isMe = u.id === me?.id;
    const editable = assignable.includes(u.role) && !isMe;
    return (
      <>
        {canMove && !['admin', 'super_admin'].includes(u.role) && (
          <>
            <button className="btn btn-sm" title="Switch who this person reports to"
              onClick={() => { setErr(''); setMoveTo(u.reports_to_id || ''); setMoveFor(u); }}>Move</button>{' '}
          </>
        )}
        {canRemove && (
          <>
            <button className="btn btn-sm" onClick={() => { setErr(''); setPwFor(u); }}
              disabled={!editable && !isMe}>Password</button>{' '}
          </>
        )}
        <button className="btn btn-sm" onClick={() => patchUser(u, { is_active: !u.is_active }, `${u.name} ${u.is_active ? 'deactivated' : 'reactivated'}`)}
          disabled={!editable}>{u.is_active ? 'Deactivate' : 'Activate'}</button>
        {canRemove && (
          <>
            {' '}
            <button className="btn btn-sm btn-danger" onClick={() => openRemove(u)}
              disabled={!editable}>Remove</button>
          </>
        )}
      </>
    );
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Team</div>
          <div className="page-subtitle">
            {canMove
              ? (companyName ? `Everyone in ${companyName}` : 'Everyone in your company')
              : 'Your team — the people who report to you, and theirs'}
            {assignable.length > 0 && ` · you can assign: ${assignable.map(r => ROLE_LABELS[r] || r).join(', ')}`}
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => { setErr(''); setForm(f => ({ ...f, reports_to_id: '' })); setShowAdd(true); }} disabled={!assignable.length}>
          + Add Person
        </button>
      </div>

      {err && !showAdd && !removeFor && !pwFor && !moveFor && <div className="alert alert-error" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={14} strokeWidth={2} /> {err}</div>}
      {msg && <div className="alert alert-success" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}><Check size={14} strokeWidth={2.5} /> {msg}</div>}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 16 }}>
        <input
          className="form-input"
          placeholder="Search by name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <div className="view-switch" role="tablist" aria-label="Team view">
          <button type="button" role="tab" aria-selected={view === 'org'} className={view === 'org' ? 'active' : ''} onClick={() => setView('org')}>
            <Network size={14} strokeWidth={2} /> Org chart
          </button>
          <button type="button" role="tab" aria-selected={view === 'list'} className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>
            <List size={14} strokeWidth={2} /> List
          </button>
        </div>
      </div>

      {loading ? (
        <div className="empty-state"><div className="empty-state-text">Loading…</div></div>
      ) : users.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Users size={40} strokeWidth={1.5} /></div>
          <div className="empty-state-title">No one here yet</div>
          <div className="empty-state-text">Add your first team member to get started.</div>
        </div>
      ) : view === 'org' ? (
        <OrgChart users={users} meId={me?.id} search={search} renderActions={renderActions} />
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Role</th><th>Reports to</th><th>Team</th><th>Sign-in</th><th>Status</th><th />
              </tr>
            </thead>
            <tbody>
              {listed.map(u => {
                const isMe = u.id === me?.id;
                const editable = assignable.includes(u.role) && !isMe;
                return (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 600 }}>
                      {u.name}
                      {isMe && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}> (you)</span>}
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>{u.email}</td>
                    <td>
                      {editable ? (
                        <select
                          className="form-input"
                          value={u.role}
                          onChange={e => patchUser(u, { role: e.target.value }, `${u.name} is now ${ROLE_LABELS[e.target.value]}`)}
                          style={{ padding: '4px 8px', fontSize: 12 }}
                        >
                          {assignable.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                        </select>
                      ) : <RoleBadge role={u.role} />}
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {['admin', 'super_admin'].includes(u.role)
                        ? <span style={{ color: 'var(--text-muted)' }}>—</span>
                        : u.reports_to_name || <span style={{ color: 'var(--text-muted)' }}>Admin</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {u.team_size > 0 && <div>{u.team_size} {u.team_size === 1 ? 'person' : 'people'}</div>}
                      {u.job_count > 0 && <div>{u.job_count} {u.job_count === 1 ? 'job' : 'jobs'}</div>}
                      {!u.team_size && !u.job_count && '—'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {u.has_password ? 'Password' : <span style={{ color: 'var(--red)' }}>no password</span>}
                    </td>
                    <td>
                      <span style={{ color: u.is_active ? 'var(--success)' : 'var(--text-muted)', fontSize: 12, fontWeight: 600 }}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {renderActions(u)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add person */}
      {showAdd && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Add Person</h3>
            <div className="modal-sub">{companyName ? `to ${companyName}` : 'to your company'}</div>
            <form onSubmit={addUser}>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input className="form-input" required value={form.name} autoFocus
                  onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" required value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })} placeholder="jane@company.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value, reports_to_id: '' })}>
                  {assignable.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                </select>
              </div>
              {(canMove ? (rules[form.role] || []).length > 0 : leaderOptions(users, rules, form.role).length > 1) && (
                <div className="form-group">
                  <label className="form-label">Reports to</label>
                  {canMove ? (
                    <LeaderSelect
                      value={form.reports_to_id}
                      onChange={v => setForm({ ...form, reports_to_id: v })}
                      options={leaderOptions(users, rules, form.role)}
                      allowTop
                    />
                  ) : (
                    <select className="form-select" value={form.reports_to_id || me?.id || ''}
                      onChange={e => setForm({ ...form, reports_to_id: e.target.value === me?.id ? '' : e.target.value })}>
                      {leaderOptions(users, rules, form.role).map(u => (
                        <option key={u.id} value={u.id}>
                          {u.id === me?.id ? 'You' : u.name} · {ROLE_LABELS[u.role] || u.role}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Temporary password</label>
                <PasswordField
                  value={form.password}
                  onChange={v => setForm({ ...form, password: v })}
                  label=""
                />
              </div>

              <div className="modal-hint">
                They’ll sign in with this email and temporary password, then change it from “Forgot password?”.
              </div>
              <ModalError message={err} />
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => { setErr(''); setShowAdd(false); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Adding…' : 'Add Person'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Remove — window.confirm is blocked in embedded browsers, so this asks
          in the page instead. It also names what is lost, which a one-line
          browser prompt could not. */}
      {removeFor && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Remove {removeFor.name}?</h3>
            <div className="modal-sub">{removeFor.email} · {companyName}</div>
            <div className="modal-hint" style={{ marginTop: 12 }}>
              They lose access immediately and this cannot be undone.
            </div>
            {removeFor.team_size > 0 && (
              <div className="form-group" style={{ marginTop: 14 }}>
                <label className="form-label">
                  Move their team ({removeFor.team_size} {removeFor.team_size === 1 ? 'person' : 'people'}) to
                </label>
                <LeaderSelect
                  value={handover.team}
                  onChange={v => setHandover(h => ({ ...h, team: v }))}
                  options={handoverLeaders(removeFor)}
                  allowTop
                />
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  Need a new leader? Add them first, then come back.
                </div>
              </div>
            )}
            {removeFor.job_count > 0 && (
              <div className="form-group" style={{ marginTop: 14 }}>
                <label className="form-label">
                  Move their jobs ({removeFor.job_count}) and candidates to
                </label>
                <LeaderSelect
                  value={handover.jobs}
                  onChange={v => setHandover(h => ({ ...h, jobs: v }))}
                  options={handoverRecruiters(removeFor)}
                  allowTop
                  topLabel="Keep with the admin only"
                />
              </div>
            )}
            <ModalError message={err} />
            <div className="modal-actions">
              <button type="button" className="btn" onClick={() => { setRemoveFor(null); setErr(''); }}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" disabled={busy}
                onClick={() => removeUser(removeFor)}>
                {busy ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move — switch someone's leader; their own team comes with them. */}
      {moveFor && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Move {moveFor.name}</h3>
            <div className="modal-sub">
              {ROLE_LABELS[moveFor.role] || moveFor.role} · now reports to {moveFor.reports_to_name || 'the admin'}
            </div>
            <form onSubmit={moveUser}>
              <div className="form-group" style={{ marginTop: 14 }}>
                <label className="form-label">Reports to</label>
                <LeaderSelect
                  value={moveTo}
                  onChange={setMoveTo}
                  options={leaderOptions(users, rules, moveFor.role, moveFor.id)}
                  allowTop
                />
              </div>
              <div className="modal-hint">
                {moveFor.team_size > 0
                  ? `Their team of ${moveFor.team_size} moves with them, and so do the jobs and candidates of that team.`
                  : 'Their jobs and candidates move with them to the new branch.'}
              </div>
              <ModalError message={err} />
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => { setErr(''); setMoveFor(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy || moveTo === (moveFor.reports_to_id || '')}>
                  {busy ? 'Moving…' : 'Move'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Set password */}
      {pwFor && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Set password</h3>
            <div className="modal-sub">{pwFor.name} · {pwFor.email}</div>
            <form onSubmit={savePassword}>
              <PasswordField
                value={newPw}
                onChange={setNewPw}
                label="New password"
                autoFocus
              />
              <div className="modal-hint">
                This replaces their current password immediately and cancels any pending reset.
              </div>
              <ModalError message={err} />
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => { setErr(''); setPwFor(null); setNewPw(''); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Saving…' : 'Set Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
